import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { DriverLane } from './DriverLane';
import { MoveRisk, DriverWithMoves, DisplaySettings, MoveGroup, DragState, GhostSuggestion } from './types';
import styles from './RiskSection.module.css';

export interface RiskSectionProps {
  sectionId?: string;
  title: string;
  icon: React.ReactNode;
  headerClass: string;
  drivers: DriverWithMoves[];
  showCompleted: boolean;
  displaySettings: DisplaySettings;
  isSectionExpanded: boolean;
  onToggleSection: () => void;

  /** Layout orientation passed through to each DriverLane */
  orientation?: 'horizontal' | 'vertical';

  /** Render prop for location map passed through to each DriverLane */
  renderLocationMap?: (props: {
    driverLat: number;
    driverLng: number;
    originLat?: number | null;
    originLng?: number | null;
    destLat?: number | null;
    destLng?: number | null;
  }) => React.ReactNode;

  // All interaction callbacks are optional — passed through to DriverLane
  onProClick?: (pronumber: number, legnum: number) => void;
  onRemove?: (move: MoveRisk, group?: MoveGroup) => void;
  onQuickAssign?: (move: MoveRisk, targetUnitnumber: string) => void;
  onDragStart?: (e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetDriver: string) => void;
  onReorder?: (driverUnitnumber: string, newOrder: MoveGroup[]) => void;
  dragTargetDriver?: string | null;
  dragState?: DragState | null;
  openAvailableMovesDrawer?: (unitNumber: string, driverName: string, mapData?: {
    fromName: string;
    fromCityState: string;
    fromLat: number;
    fromLng: number;
    toName: string;
    toCityState: string;
    toLat: number;
    toLng: number;
    travelMinutes: number;
  }) => void;
  onOpenDeadheadMap?: (prevMove: MoveRisk, nextMove: MoveRisk, travelMinutes: number, driverUnit: string, driverName: string) => void;
  onGhostCardClick?: (suggestion: GhostSuggestion) => void;
  showGhostCards?: boolean;
  userEmail?: string;
  pendingReorderDrivers?: Set<string>;
  onApplyReorder?: (driverUnitnumber: string) => void;
  onCancelReorder?: (driverUnitnumber: string) => void;
  isToday?: boolean;
  dispatchRequestUnitnumbers?: Set<string>;
  /** Clear a driver's open work request when there's nothing to give them */
  onAcknowledgeRequest?: (unitnumber: string) => void;
  /** Add/edit a driver's status note from the lane header's comment icon */
  onEditComment?: (unitnumber: string, driverName: string, currentComment: string | null) => void;
  /** The page-level date (YYYY-MM-DD), passed through to DriverLane for day cycling */
  pageDate?: string;
}

export function RiskSection({
  sectionId,
  title,
  icon,
  headerClass,
  drivers,
  showCompleted,
  displaySettings,
  isSectionExpanded,
  onToggleSection,
  orientation,
  renderLocationMap,
  onProClick,
  onRemove,
  onQuickAssign,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onReorder,
  dragTargetDriver,
  dragState,
  openAvailableMovesDrawer,
  onOpenDeadheadMap,
  onGhostCardClick,
  showGhostCards,
  pendingReorderDrivers,
  onApplyReorder,
  onCancelReorder,
  isToday = true,
  dispatchRequestUnitnumbers,
  onAcknowledgeRequest,
  onEditComment,
  pageDate,
}: RiskSectionProps) {
  const isSuperCompact = !!displaySettings.cardOptions?.compactView && !!displaySettings.cardOptions?.superCompactView;

  if (drivers.length === 0) return null;

  const moveCount = drivers.reduce((sum, d) => sum + d.moves.length, 0);

  return (
    <div id={sectionId}>
      <div
        className={`${styles.sectionHeader} ${headerClass} cursor-pointer`}
        onClick={onToggleSection}
      >
        <ChevronRightIcon className={`w-5 h-5 transition-transform ${isSectionExpanded ? 'rotate-90' : ''}`} />
        {icon}
        <span>{title}</span>
        <span className="text-sm font-normal ml-1">({drivers.length} driver{drivers.length !== 1 ? 's' : ''}, {moveCount} move{moveCount !== 1 ? 's' : ''})</span>
      </div>
      {isSectionExpanded && (
        <div className={`${styles.sectionContent}${isSuperCompact ? ` ${styles.sectionContentSuperCompact}` : ''}`}>
          {drivers.map((driver, index) => (
            <DriverLane
              key={driver.unitnumber}
              driver={driver}
              index={index + 1}
              showCompleted={showCompleted}
              displaySettings={displaySettings}
              orientation={orientation}
              renderLocationMap={renderLocationMap}
              onProClick={onProClick}
              onRemove={onRemove}
              onQuickAssign={onQuickAssign}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onReorder={onReorder}
              isDragTarget={dragTargetDriver === driver.unitnumber}
              dragState={dragState}
              openAvailableMovesDrawer={openAvailableMovesDrawer}
              onOpenDeadheadMap={onOpenDeadheadMap}
              onGhostCardClick={onGhostCardClick}
              showGhostCards={showGhostCards}
              hasPendingReorder={pendingReorderDrivers?.has(driver.unitnumber)}
              onApplyReorder={onApplyReorder}
              onCancelReorder={onCancelReorder}
              isToday={isToday}
              hasDispatchRequest={dispatchRequestUnitnumbers?.has(driver.unitnumber)}
              onAcknowledgeRequest={onAcknowledgeRequest}
              onEditComment={onEditComment}
              pageDate={pageDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RiskSection;
